import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { setI18nLanguage } from '@/i18n/helpers';

import { useHouseholdStore } from '@/features/household/store/household';

/**
 * Composable to sync website language with household settings
 * This should be called once at app initialization
 */
export function useLanguageSync() {
  const { locale } = useI18n();
  const householdStore = useHouseholdStore();

  const error = ref<Error | null>(null);

  const changeLanguage = async (newLanguage: string) => {
    if (newLanguage === locale.value) return;

    try {
      // await loadLocaleMessages(newLanguage)
      setI18nLanguage(newLanguage);
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Failed to load language');
      console.error('Language change failed:', err);
      throw err;
    }
  }

  // Watch for household language changes and update website language
  watch(
    () => householdStore.household?.languageCode,
    async (newLanguageCode) => {
      if (newLanguageCode && newLanguageCode !== locale.value) {
        await changeLanguage(newLanguageCode);
      }
    },
    { immediate: true }
  );

  // Function to manually sync language (useful after settings updates)
  const syncLanguage = async () => {
    await nextTick();
    const householdLanguageCode = householdStore.household?.languageCode;
    if (householdLanguageCode && householdLanguageCode !== locale.value) {
      await changeLanguage(householdLanguageCode);
    }
  };

  return {
    syncLanguage,
  };
}
